/**
 * Custom NotesComponent - Override Component
 * Auto generated overrides from shared library
 */
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

type NoteItem = { id: number; label: string; text: string };

interface NotesData {
  notes?: NoteItem[];
  notesApiUrl?: string;
  marginTop?: string;
  widgetId?: string;
  apiUrl?: string;
  method?: 'get' | 'post' | string;
  responsePath?: string;
  notesMappingKey?: string;
  noteLabel?: string;
  footer?: {
    displayFooter?: boolean;
    text?: string;
  };
}

@Component({
  selector: 'app-notes',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notes.html',
  styleUrls: ['./notes.css']
})
export class NotesComponent implements OnInit, OnDestroy {
  @Input() data?: any;
  notes: NoteItem[] = [];
  private destroy$ = new Subject<void>();

  public get footer(): any {
    return (this.data && (this.data as any).footer) || null;
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService
  ) {}

  ngOnInit(): void {
    if (this.isTcoNotesWidget()) {
      this.loadTcoBackgroundNote();
      this.globalFilterService.filter$
        .pipe(takeUntil(this.destroy$))
        .subscribe((filters) => {
          if (Object.keys(filters || {}).length === 0) {
            return;
          }
          this.loadTcoBackgroundNote(filters);
        });
      return;
    }

    this.loadStaticNotes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isTcoNotesWidget(): boolean {
    return `${this.data?.widgetId ?? ''}`.trim() === 'repower-notes-override';
  }

  private loadTcoBackgroundNote(filters?: any): void {
    const apiConfig = this.buildTcoApiConfig();

    this.configService.getConfig(
      apiConfig.apiUrl,
      apiConfig,
      { ...filters, useQueryParams: false },
      apiConfig.method
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        const source = this.getByPath(response, apiConfig.responsePath);
        const mappingKey = `${this.data?.notesMappingKey ?? 'tco_background'}`.trim();
        const text = `${this.getByPath(source, mappingKey) ?? ''}`.trim();

        if (!text) {
          this.loadStaticNotes();
          return;
        }

        this.notes = [
          {
            id: 1,
            label: `${this.data?.noteLabel ?? 'TCO Summary'}`,
            text,
          },
        ];
      },
      error: () => this.loadStaticNotes(),
    });
  }

  private buildTcoApiConfig(): any {
    const mine = this.resolveMine();
    const engineModel = this.resolveEngineModel();

    return {
      ...this.data,
      apiUrl: this.data?.apiUrl || '/spriced/platform',
      method: this.data?.method || 'post',
      responsePath: this.data?.responsePath || 'responseList.0.ui-data.0.data.0',
      skipDefaultPayload: true,
      customPayload: {
        requestData: {
          mine,
          engine_model_key: engineModel,
        },
        responseList: [],
        entity: 'mines',
        batchId: 'test',
        workflowId: 'ABC-123',
        endpoints: {
          functionName: 'tcoDashboard',
          version: '1.0',
        },
        communicationType: 'rest',
        version: '1.0',
      },
    };
  }

  private resolveMine(): string {
    const navState = (window?.history?.state || {}) as any;
    return `${navState?.selectedMineName ?? navState?.selectedMine?.name ?? 'Fort Hills'}`.trim();
  }

  private resolveEngineModel(): string {
    const navState = (window?.history?.state || {}) as any;
    return `${
      navState?.selectedEngineModelKey ??
      navState?.engine_model_key ??
      navState?.selectedEngine ??
      navState?.engine_model ??
      'QSK78'
    }`.trim();
  }

  private loadStaticNotes(): void {
    if (Array.isArray(this.data?.notes) && this.data.notes.length > 0) {
      this.notes = this.data.notes;
      return;
    }

    const notesUrl = this.data?.notesApiUrl || '/assets/notes/notes-config.json';
    this.http.get<{ notes: NoteItem[] }>(notesUrl).subscribe({
      next: (response) => {
        this.notes = response?.notes ?? [];
      },
      error: () => {
        this.notes = [];
      },
    });
  }

  private getByPath(source: any, path: string): any {
    if (!path) {
      return source;
    }

    return path.split('.').reduce((acc: any, key: string) => {
      if (acc === null || acc === undefined) {
        return undefined;
      }

      if (/^\d+$/.test(key)) {
        return acc[Number(key)];
      }

      return acc[key];
    }, source);
  }

  trackById(_: number, note: { id: number }): number {
    return note.id;
  }

}
